import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Users, Target, Zap, TrendingUp, Award, Building2, Wrench } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// Value Proposition Card
const ValueCard = ({ icon: Icon, title, description, image, color }) => (
  <div 
    className="card overflow-hidden h-full group"
    style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
  >
    {/* Image */}
    <div className="h-40 overflow-hidden">
      <img 
        src={image} 
        alt={title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
    </div>
    <div className="p-6">
      <div 
        className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
        style={{ background: color }}
      >
        <Icon size={28} style={{ color: "white" }} />
      </div>
      <h3 
        className="text-xl font-bold mb-2"
        style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
      >
        {title}
      </h3>
      <p style={{ color: "var(--text-200)", fontSize: "0.95rem" }}>{description}</p>
    </div>
  </div>
);

// Main About Page Component
const AboutPage = () => {
  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative min-h-[70vh] flex items-center">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.3 }}
            />
            <div 
              className="absolute inset-0"
              style={{ 
                background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, rgba(0,12,37,0.6) 50%, var(--background-100) 100%)" 
              }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-20">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Award size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Our Story</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                About <span className="text-gradient">MachineryMarketed</span>
              </h1>
              <p 
                className="text-2xl md:text-3xl font-bold"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Built by dealership people,{" "}
                <span className="text-gradient">for dealership people.</span>
              </p>
            </div>
          </div>
        </section>

        {/* Two Column Story Section with Image */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              {/* Image Side */}
              <div className="relative">
                <div 
                  className="rounded-2xl overflow-hidden shadow-2xl"
                  style={{ boxShadow: "0 25px 50px rgba(0, 0, 0, 0.4)" }}
                >
                  <img 
                    src="https://www.machinerymarketed.com/agri-4.jpg" 
                    alt="Equipment in yard"
                    className="w-full h-auto"
                  />
                </div>
                {/* Floating Badge */}
                <div 
                  className="absolute -bottom-6 -right-6 p-6 rounded-2xl"
                  style={{ 
                    background: "var(--primary-100)",
                    boxShadow: "0 10px 30px rgba(15, 95, 255, 0.3)"
                  }}
                >
                  <Building2 size={40} style={{ color: "white" }} />
                </div>
              </div>
              
              {/* Content Side */}
              <div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                >
                  Years on the Ground
                </h2>
                <div className="space-y-4" style={{ color: "var(--text-200)", lineHeight: "1.8" }}>
                  <p className="text-lg">
                    We've spent years on the ground in the machinery industry — selling, appraising, 
                    managing trades, and dealing with the day-to-day reality of running a dealership.
                  </p>
                  <p className="text-lg">
                    We've also worked closely with good technology and seen first-hand how the right 
                    tools can make a real difference when they're designed properly for the job.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Statement with Background */}
        <section className="py-20 relative">
          <div 
            className="absolute inset-0 z-0"
            style={{
              background: "radial-gradient(ellipse at center, rgba(15, 95, 255, 0.08) 0%, transparent 70%)"
            }}
          />
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Wrench size={48} style={{ color: "var(--primary-300)", margin: "0 auto 24px" }} />
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Why We Exist
              </h2>
              <p 
                className="text-xl mb-8"
                style={{ color: "var(--text-200)", lineHeight: "1.8" }}
              >
                This platform exists for one reason: to help dealerships turn their trades faster, 
                appraise more accurately, and free up their teams to focus on customers and the 
                industry — not admin.
              </p>
            </div>
          </div>
        </section>

        {/* The Problem & Solution Section with Images */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {/* The Problem */}
              <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
                <div className="order-2 lg:order-1">
                  <div 
                    className="rounded-2xl overflow-hidden"
                    style={{ boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)" }}
                  >
                    <img 
                      src="https://www.machinerymarketed.com/agri-1.jpg" 
                      alt="Equipment on ground"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-4"
                    style={{ background: "rgba(255, 100, 100, 0.2)", color: "#ff6464" }}
                  >
                    The Problem
                  </span>
                  <h3 
                    className="text-3xl font-bold mb-6"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    Forgotten Hostage Cash
                  </h3>
                  <p 
                    className="text-lg mb-4"
                    style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                  >
                    We know how much time gets soaked up listing, relisting, editing ads, chasing 
                    photos, and pushing machines across multiple platforms.
                  </p>
                  <p 
                    className="text-lg"
                    style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                  >
                    We also know how easily a trade can end up parked in a yard, behind a shed, or 
                    at someone's place — slowly becoming forgotten hostage cash until it shows up 
                    in the financials.
                  </p>
                </div>
              </div>

              {/* The Solution */}
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-4"
                    style={{ background: "rgba(15, 95, 255, 0.2)", color: "var(--primary-300)" }}
                  >
                    Our Solution
                  </span>
                  <h3 
                    className="text-3xl font-bold mb-6"
                    style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
                  >
                    One Clean System
                  </h3>
                  <p 
                    className="text-lg mb-4"
                    style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                  >
                    MachineryMarketed creates a clean, repeatable system that connects appraisal, 
                    valuation, and marketing in one flow.
                  </p>
                  <p 
                    className="text-lg"
                    style={{ color: "var(--text-200)", lineHeight: "1.8" }}
                  >
                    The result is better visibility, faster movement, and fewer machines slipping 
                    through the cracks.
                  </p>
                </div>
                <div>
                  <div 
                    className="rounded-2xl overflow-hidden"
                    style={{ boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)" }}
                  >
                    <img 
                      src="https://www.machinerymarketed.com/app-appraisal.jpg" 
                      alt="App interface"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Closing Statement with Image Background */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/tractor_loader_pair.jpg" 
              alt="Tractor and loader"
              className="w-full h-full object-cover"
              style={{ opacity: 0.15 }}
            />
            <div 
              className="absolute inset-0"
              style={{ background: "linear-gradient(to right, var(--background-100) 0%, transparent 50%, var(--background-100) 100%)" }}
            />
          </div>
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <p 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                At the end of the day, moving metal is what we're all about
              </p>
              <p 
                className="text-2xl"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--primary-300)" }}
              >
                Helping dealerships become more robust, more profitable, and keep the wheel spinning faster.
              </p>
            </div>
          </div>
        </section>

        {/* Value Propositions with Images */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                What We Stand For
              </h2>
              <p style={{ color: "var(--text-200)" }}>
                Built on real experience, designed for real results
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              <ValueCard 
                icon={Users}
                title="Industry People"
                description="Built by those who've worked in dealerships, not just software developers"
                image="https://www.machinerymarketed.com/agri-1.jpg"
                color="#0038a8"
              />
              <ValueCard 
                icon={Target}
                title="Focused Solution"
                description="One clear purpose: faster trades, better appraisals, simpler marketing"
                image="https://www.machinerymarketed.com/agri-3.jpg"
                color="#0049db"
              />
              <ValueCard 
                icon={Zap}
                title="Real Impact"
                description="Tools that actually save time and free up your team"
                image="https://www.machinerymarketed.com/agri-4.jpg"
                color="#0f5fff"
              />
              <ValueCard 
                icon={TrendingUp}
                title="Better Results"
                description="More robust dealerships, more profitable operations"
                image="https://www.machinerymarketed.com/agri-6.jpg"
                color="#0038a8"
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden"
              style={{ maxWidth: "900px", margin: "0 auto" }}
            >
              {/* Background */}
              <div 
                className="absolute inset-0 z-0"
                style={{
                  background: "linear-gradient(135deg, var(--primary-100) 0%, var(--primary-300) 100%)"
                }}
              />
              <div 
                className="absolute inset-0 z-0 opacity-20"
                style={{
                  backgroundImage: "url('https://www.machinerymarketed.com/blazing-arrow.jpg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />
              
              {/* Content */}
              <div className="relative z-10 p-12 text-center">
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white" }}
                >
                  Want to learn more?
                </h2>
                <p className="mb-8 text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                  See how MachineryMarketed can transform your dealership's trade-in process.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link 
                    to="/faq" 
                    className="px-6 py-3 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "transparent", 
                      border: "2px solid white",
                      color: "white"
                    }}
                  >
                    Read our FAQ
                  </Link>
                  <Link 
                    to="/#contact" 
                    className="px-6 py-3 rounded-lg font-semibold transition-all"
                    style={{ 
                      background: "white", 
                      color: "var(--primary-100)"
                    }}
                  >
                    Request a Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
